import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common/StatusBadge";
import CorporateDetailModal from "@/components/corporates/CorporateDetailModal";
import { CheckCircle2, XCircle, Loader2, Hotel, Eye, FileClock } from "lucide-react";

const TABS = ["Pending", "Approved", "Rejected"];

export default function TariffApprovalHub({ properties, reloadNotifications }) {
  const { isManagement } = useAuth();
  const [tab, setTab] = useState("Pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/corporates", { params: { tariff: tab } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.post(`/corporates/${id}/approve-tariff`); toast.success("Approved"); load(); reloadNotifications(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doReject = async () => {
    try { await api.post(`/corporates/${rejecting}/reject-tariff`, { note }); toast.success("Rejected"); setRejecting(null); setNote(""); load(); reloadNotifications(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight">Tariff Approvals</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isManagement ? "Review and action hotel-wise tariff submissions." : "Track the approval status of your submitted tariffs."}
        </p>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`approval-tab-${t.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              tab === t ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"
            }`}>{t}</button>
        ))}
      </div>

      {loading ? (
        <div className="h-40 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-16 text-center text-muted-foreground">
          <FileClock className="h-8 w-8 mx-auto mb-2 opacity-40" /> No {tab.toLowerCase()} tariffs.
        </div>
      ) : (
        <div className="grid gap-4">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-5" data-testid={`approval-card-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{r.company_name}</h3>
                    <StatusBadge status={r.tariff_status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{r.district}, {r.state} · Submitted by {r.created_by_name}</p>
                  {r.tariff_status === "Rejected" && r.tariff_rejection_note && (
                    <p className="text-xs text-rose-700 mt-1">Reason: {r.tariff_rejection_note}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setModal(r.id)} data-testid={`approval-view-${r.id}`}><Eye className="h-4 w-4 mr-1.5" /> Details</Button>
                  {isManagement && r.tariff_status !== "Approved" && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(r.id)} data-testid={`approval-approve-${r.id}`}><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</Button>
                  )}
                  {isManagement && r.tariff_status !== "Rejected" && (
                    <Button size="sm" variant="outline" className="text-destructive border-destructive/40" onClick={() => setRejecting(r.id)} data-testid={`approval-reject-${r.id}`}><XCircle className="h-4 w-4 mr-1.5" /> Reject</Button>
                  )}
                </div>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(r.tariffs || []).slice(0, 6).map((t) => {
                  const enabled = ["ep", "cp", "map", "ap"].filter((k) => t.plans?.[k]?.enabled);
                  const summary = enabled.length
                    ? enabled.map((k) => `${k.toUpperCase()} ₹${t.plans[k].single_rate || "—"}/${t.plans[k].double_rate || "—"}`).join(" · ")
                    : (t.single_rate || t.double_rate ? `SGL ₹${t.single_rate || "—"} · DBL ₹${t.double_rate || "—"}` : "No plans set");
                  return (
                    <div key={t.property_id} className="rounded-lg border border-border bg-secondary/40 px-3 py-2">
                      <div className="text-xs font-medium flex items-center gap-1.5"><Hotel className="h-3.5 w-3.5 text-primary" /> {t.property_name}</div>
                      <div className="text-xs text-muted-foreground mt-1 font-mono">{summary}</div>
                      {(t.office_pickup_drop || t.laundry) && (
                        <div className="text-xs text-muted-foreground mt-1" data-testid={`approval-extras-${t.property_id}`}>
                          {[t.office_pickup_drop && "Pickup & Drop", t.laundry && `Laundry${t.laundry_remarks ? ` (${t.laundry_remarks})` : ""}`].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {rejecting === r.id && (
                <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50/60 p-4 space-y-2">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Rejection reason…" data-testid={`reject-note-${r.id}`} />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => { setRejecting(null); setNote(""); }}>Cancel</Button>
                    <Button size="sm" className="bg-destructive hover:bg-destructive/90" onClick={doReject} data-testid={`confirm-reject-${r.id}`}>Confirm Reject</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <CorporateDetailModal open corporateId={modal} createMode={false} properties={properties} onClose={() => setModal(null)} onSaved={() => { load(); reloadNotifications(); }} />
      )}
    </div>
  );
}
